import { useEffect, useMemo, useState, useCallback } from "react";
import api from "@/api/client";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Upload, FileSpreadsheet, RefreshCw, ArrowUpDown, ArrowUp, ArrowDown,
  AlertTriangle, CheckCircle2, Search,
} from "lucide-react";
import { toast } from "sonner";

const REASON_TONES = {
  missing_email: "bg-rose-50 text-rose-800 border-rose-200",
  non_ahj_domain: "bg-amber-50 text-amber-800 border-amber-200",
  non_ahj_emails_only: "bg-amber-50 text-amber-800 border-amber-200",
  no_ahj_email_in_row: "bg-slate-100 text-slate-700 border-slate-200",
  no_ahj_email_scraped: "bg-slate-100 text-slate-700 border-slate-200",
  invalid_state_abbr: "bg-amber-50 text-amber-800 border-amber-200",
  matcher_unmatched: "bg-rose-50 text-rose-800 border-rose-200",
};

function fmtSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function StatTile({ label, value, tone = "slate", testId }) {
  const tones = {
    slate: "bg-slate-50 text-slate-900",
    emerald: "bg-emerald-50 text-emerald-900",
    amber: "bg-amber-50 text-amber-900",
    rose: "bg-rose-50 text-rose-900",
  };
  return (
    <div className={`rounded-lg px-4 py-3 ${tones[tone]}`} data-testid={testId}>
      <div className="text-[10px] uppercase tracking-wider opacity-70">{label}</div>
      <div className="text-2xl font-outfit font-bold tabular-nums mt-0.5">{value ?? 0}</div>
    </div>
  );
}

function SortHeader({ label, field, sort, setSort, align = "left" }) {
  const active = sort.field === field;
  const Icon = !active ? ArrowUpDown : sort.dir === "asc" ? ArrowUp : ArrowDown;
  return (
    <th
      className={`px-3 py-2 text-[10px] uppercase tracking-wider text-slate-600 cursor-pointer select-none text-${align} whitespace-nowrap`}
      onClick={() => setSort({ field, dir: active && sort.dir === "asc" ? "desc" : "asc" })}
      data-testid={`flagged-sort-${field}`}
    >
      <span className="inline-flex items-center gap-1">
        {label} <Icon className={`w-3 h-3 ${active ? "text-slate-900" : "text-slate-400"}`} />
      </span>
    </th>
  );
}

export default function XlsxIngestPanel() {
  const [report, setReport] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("input");  // "input" | "writes"
  const [q, setQ] = useState("");
  const [sort, setSort] = useState({ field: "state", dir: "asc" });

  const loadLatest = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/ahj/import-xlsx/latest");
      setReport(data.report);
    } catch (_e) { /* non-fatal */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadLatest(); }, [loadLatest]);

  const handleUpload = async (file) => {
    setUploading(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/admin/ahj/import-xlsx", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(
        `Imported ${data.stats.imported} · ${data.stats.matched_place} place + ${data.stats.matched_county} county + ${data.stats.unmatched} unmatched · ${data.flagged_input_rows.length} input rows flagged`
      );
      setReport(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Import failed");
    } finally {
      setUploading(false);
    }
  };

  const flagged = useMemo(() => {
    if (!report) return [];
    return activeTab === "input" ? report.flagged_input_rows || [] : report.flagged_writes || [];
  }, [report, activeTab]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    let rows = flagged;
    if (needle) {
      rows = rows.filter((r) => [
        r.state, r.email, r.jurisdiction, r.jurisdiction_hint, r.reason, r.matcher_reason, r.name,
      ].some((v) => (v || "").toString().toLowerCase().includes(needle)));
    }
    const dir = sort.dir === "asc" ? 1 : -1;
    return [...rows].sort((a, b) => {
      const av = (a[sort.field] ?? "").toString().toLowerCase();
      const bv = (b[sort.field] ?? "").toString().toLowerCase();
      if (av === bv) return 0;
      return av < bv ? -dir : dir;
    });
  }, [flagged, q, sort]);

  const byState = useMemo(() => {
    const counts = {};
    flagged.forEach((r) => {
      const s = (r.state || "—").toUpperCase();
      counts[s] = (counts[s] || 0) + 1;
    });
    return Object.entries(counts).sort((a, b) => b[1] - a[1]);
  }, [flagged]);

  return (
    <div className="mt-6 bg-white border border-slate-200 rounded-xl p-6" data-testid="xlsx-ingest-panel">
      <div className="flex items-start justify-between flex-wrap gap-4 mb-5">
        <div>
          <h3 className="font-outfit text-lg font-semibold text-slate-900 flex items-center gap-2">
            <FileSpreadsheet className="w-4 h-4 text-emerald-600" />Legacy xlsx ingest
          </h3>
          <p className="text-xs text-slate-500 mt-1 max-w-2xl">
            Drop a workbook with sheets <strong>AHJ Contacts Directory</strong>, <strong>AHJ Interactions Log</strong>, and <strong>Customer Threads</strong>.
            We de-dupe, auto-link to a jurisdiction, and surface every row that didn't map cleanly below — sortable by state so you can prioritize by region.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={loadLatest} disabled={loading || uploading} data-testid="xlsx-reload-btn">
            <RefreshCw className={`w-3.5 h-3.5 mr-2 ${loading ? "animate-spin" : ""}`} />Reload
          </Button>
          <label className="cursor-pointer">
            <input
              type="file"
              accept=".xlsx"
              data-testid="xlsx-input"
              className="hidden"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) handleUpload(f); e.target.value = ""; }}
              disabled={uploading}
            />
            <Button asChild data-testid="xlsx-upload-btn" disabled={uploading}
              className="bg-slate-900 hover:bg-slate-800 text-white pointer-events-none">
              <span>
                {uploading ? <RefreshCw className="w-3.5 h-3.5 mr-2 animate-spin" /> : <Upload className="w-3.5 h-3.5 mr-2" />}
                Upload .xlsx
              </span>
            </Button>
          </label>
        </div>
      </div>

      {report ? (
        <>
          {/* Stats row */}
          <div className="grid grid-cols-2 sm:grid-cols-6 gap-3 mb-5">
            <StatTile label="Processed" value={report.stats?.upserted ?? report.stats?.imported} tone="emerald" testId="stat-imported" />
            <StatTile label="Place matched" value={report.stats?.matched_place} tone="emerald" testId="stat-matched-place" />
            <StatTile label="County matched" value={report.stats?.matched_county} testId="stat-matched-county" />
            <StatTile label="Unmatched" value={report.stats?.unmatched} tone="rose" testId="stat-unmatched" />
            <StatTile label="Flagged input" value={report.stats?.flagged_input_rows ?? report.flagged_input_rows?.length} tone="amber" testId="stat-flagged-input" />
            <StatTile label="Sheets seen" value={report.sheets_seen?.length} testId="stat-sheets" />
          </div>

          {/* Tab switcher */}
          <div className="flex gap-2 border-b border-slate-200 mb-3">
            <button
              type="button"
              onClick={() => setActiveTab("input")}
              className={`px-3 py-2 text-xs font-semibold border-b-2 -mb-px transition ${activeTab === "input" ? "border-slate-900 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-700"}`}
              data-testid="flagged-tab-input"
            >
              Flagged input rows ({report.flagged_input_rows?.length || 0})
            </button>
            <button
              type="button"
              onClick={() => setActiveTab("writes")}
              className={`px-3 py-2 text-xs font-semibold border-b-2 -mb-px transition ${activeTab === "writes" ? "border-slate-900 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-700"}`}
              data-testid="flagged-tab-writes"
            >
              Matcher-unmatched ({report.flagged_writes?.length || 0})
            </button>
          </div>

          {/* Search + by-state pills + bulk-action toolbar */}
          <div className="flex items-start gap-3 flex-wrap mb-3">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="w-3.5 h-3.5 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <Input
                placeholder="Filter by state, email, jurisdiction, reason…"
                value={q} onChange={(e) => setQ(e.target.value)}
                className="pl-8 h-8 text-sm"
                data-testid="flagged-search"
              />
            </div>
            <div className="flex flex-wrap gap-1.5">
              {byState.slice(0, 12).map(([st, n]) => (
                <button
                  key={st}
                  onClick={() => setQ(st === "—" ? "" : st)}
                  className={`text-[10px] font-mono px-2 py-1 rounded-full border transition ${q.toUpperCase() === st ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 hover:bg-slate-100"}`}
                  data-testid={`flagged-state-chip-${st}`}
                >
                  {st} <span className={q.toUpperCase() === st ? "text-slate-300" : "text-slate-500"}>{n}</span>
                </button>
              ))}
            </div>
            {q && q.length === 2 && q === q.toUpperCase() && activeTab === "input" && (
              <Button
                size="sm" variant="outline" className="h-8 text-xs"
                onClick={async () => {
                  try {
                    const { data } = await api.post(`/admin/ahj/flagged/sentinelize?state=${q}`);
                    toast.success(
                      `${data.created} placeholder contacts created · ${data.skipped_existing} already existed · ${data.candidates_found} candidates`
                    );
                    loadLatest();
                  } catch (e) { toast.error(e.response?.data?.detail || "Sentinelize failed"); }
                }}
                data-testid="sentinelize-btn"
              >
                Synthesize info@ sentinels for {q}
              </Button>
            )}
          </div>

          {/* Flagged rows table */}
          {filtered.length === 0 ? (
            <div className="rounded-md border border-dashed border-slate-300 bg-slate-50 px-4 py-8 text-center text-xs text-slate-500" data-testid="flagged-empty">
              {q ? "No matches for the current filter." :
                activeTab === "writes" && (report.flagged_writes?.length || 0) === 0 ? (
                  <span><CheckCircle2 className="w-4 h-4 inline mr-1 text-emerald-600" />Every contact mapped to a jurisdiction — nothing to review here.</span>
                ) : <span><AlertTriangle className="w-4 h-4 inline mr-1 text-amber-500" />No flagged rows yet — upload a workbook above.</span>}
            </div>
          ) : (
            <div className="overflow-x-auto border border-slate-200 rounded-lg" data-testid="flagged-table">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <SortHeader label="State" field="state" sort={sort} setSort={setSort} />
                    <SortHeader label="Reason" field={activeTab === "input" ? "reason" : "matcher_reason"} sort={sort} setSort={setSort} />
                    <SortHeader label={activeTab === "input" ? "Sheet" : "Email"} field={activeTab === "input" ? "sheet" : "email"} sort={sort} setSort={setSort} />
                    <SortHeader label="Jurisdiction" field={activeTab === "input" ? "jurisdiction" : "jurisdiction_hint"} sort={sort} setSort={setSort} />
                    <SortHeader label={activeTab === "input" ? "Row email" : "Name"} field={activeTab === "input" ? "email" : "name"} sort={sort} setSort={setSort} />
                    {activeTab === "input" && <th className="px-3 py-2 text-[10px] uppercase tracking-wider text-slate-600 text-right">Row #</th>}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((r, i) => (
                    <tr key={i} className="border-t border-slate-100 hover:bg-slate-50/50" data-testid={`flagged-row-${i}`}>
                      <td className="px-3 py-2 font-mono text-xs">{r.state || "—"}</td>
                      <td className="px-3 py-2">
                        <Badge variant="outline" className={`text-[10px] font-mono ${REASON_TONES[r.reason] || ""}`}>
                          {r.reason || r.matcher_reason || "—"}
                        </Badge>
                      </td>
                      <td className="px-3 py-2 text-xs text-slate-600 truncate max-w-[180px]">
                        {activeTab === "input" ? r.sheet : r.email}
                      </td>
                      <td className="px-3 py-2 text-xs text-slate-900 truncate max-w-[260px]">
                        {activeTab === "input" ? (r.jurisdiction || "—") : (r.jurisdiction_hint || "—")}
                      </td>
                      <td className="px-3 py-2 text-xs font-mono text-slate-700 truncate max-w-[240px]">
                        {activeTab === "input" ? (r.email || "—") : (r.name || "—")}
                      </td>
                      {activeTab === "input" && (
                        <td className="px-3 py-2 text-xs text-right tabular-nums text-slate-500">{r.row}</td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
              {flagged.length > filtered.length && (
                <div className="text-[10px] text-slate-500 px-3 py-2 bg-slate-50 border-t border-slate-200">
                  Showing {filtered.length} of {flagged.length} rows.
                </div>
              )}
            </div>
          )}

          {report.ran_at && (
            <div className="text-[10px] font-mono text-slate-400 mt-4">
              Last ingest: {new Date(report.ran_at).toLocaleString()} · {report.sheets_seen?.map(s => `${s.sheet} (${s.rows})`).join(" · ")}
            </div>
          )}
        </>
      ) : (
        <div className="rounded-md border border-dashed border-slate-300 bg-slate-50 px-4 py-8 text-center" data-testid="xlsx-empty">
          <FileSpreadsheet className="w-6 h-6 text-slate-400 mx-auto mb-2" />
          <div className="text-sm text-slate-700">No imports yet.</div>
          <div className="text-xs text-slate-500 mt-1">Upload your AHJ Legacy Audit workbook to populate contacts + see flagged rows here.</div>
        </div>
      )}
    </div>
  );
}
